import React from 'react';
import styled from 'styled-components';
import Img from 'gatsby-image/withIEPolyfill';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import Container from '../../Containers/Container';
import { T5D, T4R } from '../../Text';
import ImageAndPhoneContainer from './ImageAndPhoneContainer';
import TabletsContainer from './TabletsContainer';
import TabletAndPhoneContainer from './TabletAndPhoneContainer';
import PhonesContainer from './PhonesContainer';

import { paddingStyle } from '../../../styles/padding';
import { media, colors, animation } from '../../../styles/constants';

const { transitionTimingFn, animationDuration } = animation;

const transitionName = 'workdetailPaning';

const WorkdetailHeaderContainer = styled(Container)`
  width: 100%;
  position: relative;
  margin-bottom: 5rem;
  height: 100%;
  min-height: 95vh;

  @media (min-width: ${media.tabletPortrait}) {
    min-height: 110rem;
  }

  @media (min-width: ${media.tabletLandscape}) {
    min-height: 110rem;
  }

  @media (min-width: ${media.bigDesktop}) {
    min-height: 120rem;
  }

  > div:last-child {
    &.${transitionName}-appear-active, &.${transitionName}-enter-active {
      > div:first-child {
        transform: translate(-5rem, -7rem);
      }

      > div:last-child {
        transform: translate(5rem, -7rem);
      }
    }

    &.${transitionName}-enter-done {
      > div {
        transform: translate(0, 0);
        transition: transform ${animationDuration}ms ${transitionTimingFn};
      }
    }

    &.${transitionName}-exit {
      > div:first-child {
        transform: translate(-5rem, -7rem);
        transition: transform ${animationDuration}ms ${transitionTimingFn};
      }

      > div:last-child {
        transform: translate(5rem, -7rem);
        transition: transform ${animationDuration}ms ${transitionTimingFn};
      }
    }
  }
`;

const TitleContainer = styled.div`
  ${paddingStyle}
  display: flex;
  flex-direction: column;

  ${T5D} {
    margin-bottom: 0.5rem;
  }

  @media (min-width: ${media.tabletLandscape}) {
    max-width: 51rem;
  }

  @media (min-width: ${media.desktop}) {
    max-width: 65rem;
  }
`;

const getImageContainerByType = type => {
  if (type === 'Two Tablets') {
    return TabletsContainer;
  }

  if (type === 'Tablet and Phone') {
    return TabletAndPhoneContainer;
  }

  if (type === 'Two Phones') {
    return PhonesContainer;
  }

  if (type === 'Image and Phone') {
    return ImageAndPhoneContainer;
  }

  throw new Error(`No Workdetail Header Type found for: ${type}`);
};

const WorkdetailHeader = ({ title, subtitle, type, images }) => {
  const ImageContainer = getImageContainerByType(type);

  return (
    <WorkdetailHeaderContainer as="header">
      <TitleContainer>
        <T5D color={colors.white}>{title}</T5D>
        <T4R color={colors.white}>{subtitle}</T4R>
      </TitleContainer>
      <CSSTransition
        classNames={transitionName}
        timeout={animationDuration}
        in
        appear
      >
        <ImageContainer>
          <div>
            {images[0].src.childImageSharp ? (
              <Img
                fluid={images[0].src.childImageSharp.fluid}
                alt={images[0].alt}
                loading="eager"
              />
            ) : (
              <img src={images[0].src.publicURL} alt={images[0].alt} />
            )}
          </div>
          <div>
            {images[1].src.childImageSharp ? (
              <Img
                fluid={images[1].src.childImageSharp.fluid}
                alt={images[1].alt}
                loading="eager"
              />
            ) : (
              <img src={images[1].src.publicURL} alt={images[1].alt} />
            )}
          </div>
        </ImageContainer>
      </CSSTransition>
    </WorkdetailHeaderContainer>
  );
};

WorkdetailHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  type: PropTypes.oneOf([
    'Two Tablets',
    'Two Phones',
    'Tablet and Phone',
    'Image and Phone'
  ]).isRequired,
  images: PropTypes.array.isRequired,
  background: PropTypes.string
};

export default WorkdetailHeader;
